import CommonTable from 'app/components/Common/CommonTable';
import { createOptionTableRows } from './utils';
import { SymbolOptionChainDataType } from 'types/ApiServicesTypes';
import { PositionBaseDataType, PositionData } from '../types';
import { useTheme } from '@mui/material/styles';
import { memo, useContext, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { themeContext } from 'app/components/Context/ToggleThemeContext';

type Props = {
  optionChainData: Record<string, SymbolOptionChainDataType>;
  handleOptionClick: (
    position: PositionBaseDataType,
    contractType: string,
    strike: string,
  ) => void;
  isGreeks: boolean;
  currentStrike: number;
  positions: PositionData;
  isCE: boolean;
  date: string;
};

const OptionCustomTable = ({
  optionChainData,
  handleOptionClick,
  isGreeks,
  currentStrike,
  positions,
  isCE,
  date,
}: Props) => {
  const theme = useTheme();
  const tableRef = useRef<HTMLDivElement>(null);

  const { rows, columns } = createOptionTableRows(
    optionChainData,
    handleOptionClick,
    isGreeks,
    currentStrike,
    positions,
    isCE,
    date,
    theme,
  );
  const { isDarkTheme } = useContext(themeContext);

  // Calculate some statistics for the header
  const totalStrikes = Object.keys(optionChainData).length;
  const activePositions = Object.values(positions).flat().length; 

  // Auto-scroll to ATM call row
  useEffect(() => {
    if (
      tableRef.current &&
      optionChainData &&
      Object.keys(optionChainData).length > 0
    ) {
      const strikePrices = Object.keys(optionChainData);
      const strikeDiff = Number(strikePrices[1]) - Number(strikePrices[0]);
      const atmStrike = Math.round(currentStrike / strikeDiff) * strikeDiff;

      // Find the ATM row index
      const atmRowIndex = strikePrices.findIndex(
        keyStrike => Number(keyStrike) === atmStrike,
      );

      if (atmRowIndex !== -1) {
        // Wait for the next tick to ensure the table is rendered
        setTimeout(() => {
          const tableContainer = tableRef.current?.querySelector(
            '.MuiTableContainer-root',
          );
          if (tableContainer) {
            const rowHeight = 48; // Approximate row height
            const headerHeight = 56; // Approximate header height
            const scrollTop = atmRowIndex * rowHeight - headerHeight;

            tableContainer.scrollTo({
              top: Math.max(0, scrollTop),
              behavior: 'smooth',
            });
          }
        }, 100);
      }
    }
  }, [optionChainData, currentStrike]);

  return (
    <Box
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      ref={tableRef}
    >
      {/* Compact Table Header */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <TrendingUpIcon sx={{ fontSize: 14, color: 'success.main' }} />
              <Typography variant="caption" color="text.secondary">
                Calls:{' '}
                {
                  Object.values(optionChainData).filter(
                    item => item.CE?.close > 0,
                  ).length
                }
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <TrendingDownIcon sx={{ fontSize: 14, color: 'error.main' }} />
              <Typography variant="caption" color="text.secondary">
                Puts:{' '}
                {
                  Object.values(optionChainData).filter(
                    item => item.PE?.close > 0,
                  ).length
                }
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Chip
              label={`${totalStrikes} Strikes`}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontSize: '0.7rem', height: '20px' }}
            />
            {activePositions > 0 && (
              <Chip
                label={`${activePositions} Positions`}
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Compact Stats Row */}

      {/* Scrollable Table Container */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '0 0 12px 12px',
        }}
      >
        <CommonTable
          containerStyle={{
            height: 'calc(100vh - 200px)',
            overflow: 'auto',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: theme.palette.grey[100],
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.grey[400],
              borderRadius: '4px',
              '&:hover': {
                background: theme.palette.grey[500],
              },
            },
            '&::-webkit-scrollbar-corner': {
              background: theme.palette.grey[100],
            },
            // Compact table styling
            '& .MuiTable-root': {
              borderCollapse: 'separate',
              borderSpacing: 0,
              minWidth: '100%',
            },
            '& .MuiTableHead-root': {
              position: 'sticky',
              top: 0,
              zIndex: 1,
              '& .MuiTableRow-root': {
                backgroundColor: isDarkTheme
                  ? theme.palette.grey[50]
                  : theme.palette.grey[100],
                '& .MuiTableCell-root': {
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: theme.palette.text.primary,
                  padding: '8px 6px',
                  backgroundColor: isDarkTheme
                    ? theme.palette.grey[50]
                    : theme.palette.grey[200],
                  '&:first-of-type': {
                    borderTopLeftRadius: '8px',
                  },
                  '&:last-of-type': {
                    borderTopRightRadius: '8px',
                  },
                  // Fixed width for lots columns (3rd and 6th columns)
                  '&:nth-child(3), &:nth-child(6)': {
                    width: '120px',
                    minWidth: '120px',
                    maxWidth: '120px',
                    overflow: 'hidden',
                  },
                },
              },
            },
            '& .MuiTableBody-root': {
              '& .MuiTableRow-root': {
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 2px 8px ${theme.palette.action.hover}`,
                },
                '&:nth-of-type(even)': {
                  backgroundColor: isDarkTheme
                    ? theme.palette.grey[50]
                    : theme.palette.grey[200],
                },
                '& .MuiTableCell-root': {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  padding: '6px 6px',
                  fontSize: '0.8rem',
                  '&:first-of-type': {
                    borderLeft: '3px solid transparent',
                  },
                  // Fixed width for lots columns (3rd and 6th columns)
                  '&:nth-child(3), &:nth-child(6)': {
                    width: '120px',
                    minWidth: '120px',
                    maxWidth: '120px',
                    overflow: 'hidden',
                  },
                },
              },
              // Add padding to the last row for better spacing
              '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                paddingBottom: '16px',
              },
            },
          }}
          headerSticky={true}
          rows={rows}
          columns={columns}
          sx={{
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default memo(OptionCustomTable);
